import type { SessionStorage } from '@kk/domain';

/**
 * @kk/domain SessionStorage over window.localStorage. Every access is wrapped: a private window, blocked site
 * data or a quota error must read as "nothing stored", never as a crash (mirrors apps/mobile secureStore).
 */
export function localSessionStorage(
  store: () => Storage | undefined = () => globalThis.localStorage,
): SessionStorage {
  return {
    async get(key) {
      try {
        return store()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    async set(key, value) {
      try {
        store()?.setItem(key, value);
      } catch {
        // ignore: the session then lives only for this page
      }
    },
    async delete(key) {
      try {
        store()?.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

/** A Map-backed stand-in for tests. */
export function memorySessionStorage(): SessionStorage & { dump(): Record<string, string> } {
  const map = new Map<string, string>();
  return {
    async get(key) {
      return map.get(key) ?? null;
    },
    async set(key, value) {
      map.set(key, value);
    },
    async delete(key) {
      map.delete(key);
    },
    dump: () => Object.fromEntries(map),
  };
}
